import {useDispatch, useSelect} from "@wordpress/data";
import {useEffect, useRef, useState} from "@wordpress/element";
import {CART_STORE_KEY} from "../../constants";

export const AddToCart = ({sku, qty = 1, className = "", children = "Add to Cart", ...props}) => {
    const {addItemToCart} = useDispatch(CART_STORE_KEY);
    const [status, setStatus] = useState("idle"); // "idle" | "adding" | "added"
    const [pendingOpen, setPendingOpen] = useState(false);
    const countAtClickRef = useRef(null);
    const resetTimerRef = useRef(null);

    const cartItemCount = useSelect(
        (select) => select(CART_STORE_KEY).getCartData()?.itemsCount ?? 0,
        []
    );

    useEffect(() => {
        if (pendingOpen && countAtClickRef.current !== null && cartItemCount > countAtClickRef.current) {
            document.querySelector(".wc-block-mini-cart__button")?.click?.();
            setPendingOpen(false);
            countAtClickRef.current = null;
            setStatus("added");
            resetTimerRef.current = setTimeout(() => setStatus("idle"), 2000);
        }
    }, [cartItemCount, pendingOpen]);

    useEffect(() => () => clearTimeout(resetTimerRef.current), []);

    const handleAdd = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        clearTimeout(resetTimerRef.current);
        countAtClickRef.current = cartItemCount;
        setStatus("adding");
        addItemToCart(sku, qty).then(() => setPendingOpen(true));
    };

    const isAdding = status === "adding";
    const isAdded = status === "added";

    return (
        <>
            <button
                className={`btn btn-primary pt-btn-pill relative overflow-hidden ${status !== "idle" ? "pointer-events-none" : ""} ${className}`}
                onClick={handleAdd}
                aria-disabled={status !== "idle"}
                {...props}
            >
                {/* Label — fades out when busy */}
                <span
                    className="transition-opacity duration-300"
                    style={{opacity: isAdding || isAdded ? 0 : 1}}
                >
                        <span className="hidden sm:inline">Add to Cart</span>
                        <span className="sm:hidden">Add</span>
                </span>

                {/* Spinner — fades in on adding, fades out on added */}
                <span
                    className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                    style={{opacity: isAdding ? 1 : 0}}
                >
                    <span className="loading loading-spinner loading-sm text-white"/>
                </span>

                {/* Checkmark — fades in and draws itself when added */}
                <span
                    className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
                    style={{opacity: isAdded ? 1 : 0}}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline
                            points="4,12 9,17 20,7"
                            style={{
                                strokeDasharray: 22,
                                strokeDashoffset: isAdded ? 0 : 22,
                                transition: isAdded ? "stroke-dashoffset 0.35s ease 0.15s" : "none",
                            }}
                        />
                    </svg>
                </span>
            </button>
        </>
    );
};